import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router";
import { GroupedInvestmentCategory } from "../../types";
import { ROLES_ENUM, SVG_ENUM, PACKET_ENUM, ROUTE_ENUM } from "enums";
import { useGetUnviewedPostsCount } from "hooks/posts";
import {
  ASSET_CATEGORY_NAMES,
  AssetCategorySlug,
} from "enums/AssetCategoryEnum";
import SVG from "shared/SVG";
import { Tooltip } from "react-tooltip";
import ContextMenu from "shared/ContextMenu";
import InvestmentPhotoLightbox from "../InvestmentPhotoLightbox/InvestmentPhotoLightbox";
import InvestmentTransactionsSideModal from "../InvestmentTransactionsSideModal/InvestmentTransactionsSideModal";
import AssetDetailsSideModal from "../AssetDetailsSideModal/AssetDetailsSideModal";
import AddInvestmentTransactionModal from "../AddInvestmentTransactionModal/AddInvestmentTransactionModal";
import CloseInvestmentModal from "../CloseInvestmentModal/CloseInvestmentModal";
import {
  CLOSE_INVESTMENT_CONFIGS,
  CloseInvestmentModalConfig,
} from "../CloseInvestmentModal/closeInvestmentConfigs";
import DeleteInvestmentModal from "../DeleteInvestmentModal/DeleteInvestmentModal";
import EditInvestmentModal from "../EditInvestmentModal/EditInvestmentModal";
import { InvestmentTransactionType } from "hooks/investments/GetInvestmentTransactionsHook";
import { useGetIndustriesHook } from "hooks/industries";
import { useGetMe } from "hooks/auth";
import TransactionsSideModal from "features/companies/TransactionsSideModal/TransactionsSideModal";
import {
  AddCompanyModal,
  AddCompanyClientModal,
  DeleteCompanyModal,
  SellSharesModal,
} from "features/companies";
import { AddSharesModal } from "features/shares";
import "./AssetTile.scss";

const SLUGS_WITH_TRANSACTIONS = [
  // "stocks",
  // "shares",
  "crypto",
  "currencies",
  // "treasury_bonds",
  // "precious_metals",
  // "precious_stones",
  // "real_estate",
  // "artwork",
  // "loans",
  // "investment_policy",
];

interface AssetTileProps {
  category: GroupedInvestmentCategory;
  index: number;
  isOwn?: boolean;
  isArchived?: boolean;
  onToggleExpand: (slug: string, isExpanded: boolean) => void;
  onDragStart: (e: React.DragEvent<HTMLDivElement>, index: number) => void;
  onDragEnter: (index: number) => void;
  onDragEnd: () => void;
  onTouchStart: (index: number) => void;
  onTouchMove: (e: React.TouchEvent) => void;
  onTouchEnd: () => void;
  isDragging: boolean;
  isDragOver: boolean;
  isDragEnabled: boolean;
  pendingOpenUuid?: string | null;
  onPendingOpenComplete?: () => void;
}

const AssetTile = ({
  category,
  index,
  isOwn = false,
  isArchived = false,
  onToggleExpand,
  onDragStart,
  onDragEnter,
  onDragEnd,
  onTouchStart,
  onTouchMove,
  onTouchEnd,
  isDragging,
  isDragOver,
  isDragEnabled,
  pendingOpenUuid,
  onPendingOpenComplete,
}: AssetTileProps) => {
  const { investmentType, isExpanded, items } = category;
  const [lightboxItem, setLightboxItem] = useState<any>(null);
  const [selectedTransactionItem, setSelectedTransactionItem] =
    useState<any>(null);
  const [selectedDetailsItem, setSelectedDetailsItem] = useState<any>(null);
  const [addTransactionItem, setAddTransactionItem] = useState<{
    item: any;
    defaultType: "buy" | "sell";
    currentQuantity?: number;
  } | null>(null);
  const [editTransactionItem, setEditTransactionItem] = useState<{
    item: any;
    tx: InvestmentTransactionType;
    currentQuantity: number;
  } | null>(null);
  const [closeItem, setCloseItem] = useState<{
    item: any;
    config: CloseInvestmentModalConfig;
  } | null>(null);
  const [deleteItem, setDeleteItem] = useState<any>(null);
  const [deleteShareItem, setDeleteShareItem] = useState<any>(null);
  const [editItem, setEditItem] = useState<any>(null);
  const [selectedShareItem, setSelectedShareItem] = useState<any>(null);
  const [sellSharesItem, setSellSharesItem] = useState<any>(null);
  const [editSharesItem, setEditSharesItem] = useState<any>(null);
  const [editCompanyItem, setEditCompanyItem] = useState<any>(null);
  // ref pozwalający wrócić do sidebara po zamknięciu modali buy/sell/edit
  const shareParentRef = useRef<any>(null);
  const tileRef = useRef<HTMLDivElement>(null);
  const isDraggingRef = useRef(false);

  const isStocksOrShares =
    investmentType.slug === "stocks" || investmentType.slug === "shares";

  const { data: authorizedUser } = useGetMe();
  const isAdmin = authorizedUser?.role === ROLES_ENUM.ADMIN;

  const navigate = useNavigate();
  const companyIds = isStocksOrShares
    ? items.map((item: any) => item.id).filter((id: any) => !!id)
    : [];
  const { data: unviewedPostCount } = useGetUnviewedPostsCount({
    companyIds,
    enabled: isStocksOrShares && companyIds.length > 0,
  });
  const hasAccessToSpeaker = isOwn
    ? authorizedUser?.package !== PACKET_ENUM.BASIC &&
      authorizedUser?.package !== PACKET_ENUM.STANDARD
    : authorizedUser?.package !== PACKET_ENUM.BASIC;
  const totalUnviewedPosts =
    unviewedPostCount && isStocksOrShares
      ? Object.values(unviewedPostCount as Record<string, number>).reduce(
          (sum: number, count: number) => sum + count,
          0,
        )
      : 0;
  const handleSpeakerClick = (companyId?: number, origin?: string) => {
    if (!hasAccessToSpeaker) return;
    if (companyId) {
      sessionStorage.setItem("selectedPostsCompany", companyId.toString());
    }
    if (origin) {
      sessionStorage.setItem("selectedPostsCompanyOrigin", origin);
    }
    navigate(ROUTE_ENUM.MARKET_INFORMATION);
  };

  const { data: industriesData } = useGetIndustriesHook();
  const industryMap: Record<string, string> = {};
  if (industriesData) {
    for (const ind of industriesData) {
      industryMap[ind.value] = ind.label;
    }
  }

  const openSellModal = (shareItem: any) => {
    shareParentRef.current = shareItem;
    setSelectedShareItem(null);
    setTimeout(() => setSellSharesItem(shareItem), 100);
  };

  const openEditSharesModal = (company: any) => {
    shareParentRef.current = company;
    setSelectedShareItem(null);
    setTimeout(() => setEditSharesItem(company), 100);
  };

  const returnToSidebar = (data?: any) => {
    if (data?.availableShares !== undefined && shareParentRef.current) {
      shareParentRef.current = {
        ...shareParentRef.current,
        sharesCount: data.availableShares,
      };
    }
    setSellSharesItem(null);
    setEditSharesItem(null);
    if (shareParentRef.current)
      setTimeout(
        () => setSelectedShareItem({ ...shareParentRef.current }),
        100,
      );
  };

  const hasTransactions = SLUGS_WITH_TRANSACTIONS.includes(investmentType.slug);

  useEffect(() => {
    if (!pendingOpenUuid) return;
    const found = items.find((item: any) => item.uuid === pendingOpenUuid);
    if (!found) return;

    if (!isExpanded) {
      onToggleExpand(investmentType.slug, true);
    }

    if (isStocksOrShares) setSelectedShareItem(found);
    else if (hasTransactions) setSelectedTransactionItem(found);
    else setSelectedDetailsItem(found);

    setTimeout(() => {
      tileRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 100);

    onPendingOpenComplete?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingOpenUuid, items]);

  useEffect(() => {
    if (selectedDetailsItem) {
      const updated = items.find(
        (item: any) => item.uuid === selectedDetailsItem.uuid,
      );
      if (updated) setSelectedDetailsItem(updated);
    }
    if (selectedTransactionItem) {
      const updated = items.find(
        (item: any) => item.uuid === selectedTransactionItem.uuid,
      );
      if (updated) setSelectedTransactionItem(updated);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items]);

  const handleToggle = () => {
    onToggleExpand(investmentType.slug, !isExpanded);
  };

  type ColumnDef = {
    key: string;
    width?: number;
    suffix?: string;
    computeSuffix?: (item: any) => string;
    computePrefix?: (item: any) => string;
    label?: string;
    value?: string;
  };

  const getColumns = (): ColumnDef[] => {
    switch (investmentType.slug) {
      case "stocks":
      case "shares":
        return [
          { key: "name", width: 250, label: "Nazwa spółki" },
          { key: "industry", width: 130, label: "Branża" },
          { key: "sharesCount", width: 120, label: "Ilość sztuk" },
          {
            key: "sharesValue",
            width: 130,
            label: "Koszt nabycia",
          },
          { key: "currencyUuid", width: 100 },
          {
            key: "publicationDate",
            width: 130,
            label: "Data publikacji na giełdzie",
          },
        ];
      case "crypto":
        return [
          { key: "name", width: 250, label: "Nazwa" },
          { key: "quantity", width: 130, label: "Ilość" },
          { key: "currencyUuid", width: 120 },
        ];
      case "currencies":
        return [
          { key: "name", width: 250 },
          {
            key: "primaryValue",
            width: 130,
            label: "Kwota",
            computeSuffix: (item) =>
              item.currency?.currency ? ` ${item.currency.currency} ` : "",
            // computeSuffix: (item) => {
            //   const code = item.currency?.currency ?? "";
            //   const symbols: Record<string, string> = {
            //     USD: "$",
            //     EUR: "€",
            //     GBP: "£",
            //     CHF: "CHF",
            //     JPY: "¥",
            //   };
            //   return symbols[code] ?? code;
            // },
          },
        ];
      case "treasury_bonds":
        return [
          { key: "", width: 250, label: "Nazwa", value: "Obligacje skarbowe" },
          { key: "interestType", width: 130 },
          { key: "quantity", width: 120, label: "Ilość sztuk" },
          {
            key: "primaryValue",
            width: 130,
            label: "Koszt nabycia",
            computeSuffix: (item) =>
              item.currency ? ` ${item.currency?.currency}` : "",
          },
          { key: "currencyUuid", width: 100 },
          { key: "interestRate", width: 220 },
        ];
      case "precious_stones":
        return [
          { key: "name", width: 250 },
          {
            key: "primaryValue",
            width: 130,
            label: "Cena zakupu",
            computeSuffix: (item) =>
              item.currency?.currency ? ` ${item.currency.currency}` : "",
          },
          { key: "currencyUuid", width: 120 },
          { key: "weight", width: 130 },
          { key: "cut", width: 115 },
          { key: "color", width: 100 },
          { key: "clarity", width: 100 },
        ];
      case "precious_metals":
        return [
          { key: "name", width: 250 },
          {
            key: "primaryValue",
            label: "Cena zakupu",
            width: 130,
            computeSuffix: (item) =>
              item.currency?.currency ? ` ${item.currency.currency}` : "",
          },
          { key: "currencyUuid", width: 120 },
          { key: "weight", width: 130 },
          { key: "fineness", width: 115 },
        ];
      case "artwork":
        return [
          { key: "artworkType", width: 250, label: "Rodzaj" },
          {
            key: "primaryValue",
            width: 130,
            label: "Cena zakupu",
            computeSuffix: (item) =>
              item.currency?.currency ? ` ${item.currency.currency}` : "",
          },
          { key: "currencyUuid", width: 120 },
          { key: "name", width: 130, label: "Tytuł" },
          { key: "author", width: 115 },
          { key: "eraAge", width: 115 },
        ];
      case "real_estate":
        return [
          { key: "name", width: 250 },
          {
            key: "primaryValue",
            width: 130,
            label: "Cena zakupu",
            computeSuffix: (item) =>
              item.currency?.currency ? ` ${item.currency.currency}` : "",
          },
          { key: "currencyUuid", width: 120 },
          { key: "city", width: 130, label: "Miejscowość" },
          { key: "area", width: 115, computeSuffix: () => " m²" },
          {
            key: "pricePerSqm",
            width: 180,
            label: "Cena za m²:",
            computeSuffix: (item) =>
              item.currency?.currency ? ` ${item.currency.currency}` : "",
          },
        ];
      case "loans":
        return [
          { key: "entity", width: 250, label: "Podmiot" },
          { key: "loanType", width: 130, label: "Rodzaj" },
          {
            key: "primaryValue",
            width: 120,
            label: "Kwota",
            computeSuffix: (item) =>
              item.currency?.currency ? ` ${item.currency.currency}` : "",
          },
          { key: "interestRate", width: 130 },
          { key: "interestType", width: 115 },
          { key: "interestPayment", width: 100 },
          { key: "contractDate", width: 100 },
          { key: "durationDate", width: 100, label: "Czas trwania" },
        ];
      case "investment_policy":
        return [
          { key: "name", width: 250 },
          { key: "currencyUuid", width: 130 },
          { key: "contractDate", width: 120 },
          { key: "durationDate", width: 130 },
          { key: "premiumPayment", width: 150 },
        ];
      default:
        return [
          { key: "name", width: 250 },
          {
            key: "primaryValue",
            width: 130,
            label: "Wartość",
            computeSuffix: (item) =>
              item.currency?.currency ? ` ${item.currency.currency}` : "",
          },
          { key: "currencyUuid", width: 120 },
        ];
    }
  };

  const columnLabels: Record<string, string> = {
    quantity: "Ilość sztuk",
    name: "Nazwa",
    purchaseRate: "Kurs zakupu",
    primaryValue: "Koszt nabycia",
    currencyUuid: "Waluta",
    industry: "Branża",
    publicationDate: "Data publikacji na giełdzie",
    area: "Wielkość [w m²]",
    entity: "Podmiot",
    loanType: "Rodzaj",
    interestRate: "Wysokość oprocentowania",
    interestType: "Rodzaj oprocentowania",
    interestPayment: "Odsetki",
    weight: "Masa",
    cut: "Szlif",
    color: "Barwa",
    clarity: "Czystość",
    fineness: "Próba",
    author: "Autor",
    eraAge: "Epoka/wiek",
    contractDate: "Data zawarcia",
    durationDate: "Czas trwania",
    premiumPayment: "Składki",
    autoRenewal: "Automatyczne przedłużenie",
  };

  const columns = getColumns();
  const hasFixedWidths = columns.some((col) => col.width !== undefined);

  const INTEREST_TYPE_LABELS: Record<string, string> = {
    fixed: "Stałe",
    variable: "Zmienne",
    periodically_fixed: "Okresowo-stałe",
  };

  const LOAN_TYPE_LABELS: Record<string, string> = {
    borrowed: "Zaciągnięta",
    lent: "Udzielona",
  };

  const PAYMENT_LABELS: Record<string, string> = {
    monthly: "Miesięczne",
    quarterly: "Kwartalne",
    yearly: "Roczne",
    at_end_of_year: "Na koniec roku",
    at_end: "Na koniec roku",
  };

  const detailsFields = [
    "area",
    "weight",
    "cut",
    "color",
    "clarity",
    "fineness",
    "artworkType",
    "author",
    "eraAge",
    "interestType",
    "interestPayment",
    "contractDate",
    "durationDate",
    "premiumPayment",
    "autoRenewal",
    "publicationDate",
  ];

  const PROPERTY_TYPE_LABELS: Record<string, string> = {
    apartment: "Mieszkanie",
    house: "Dom",
    land: "Działka",
  };

  const METAL_TYPE_LABELS: Record<string, string> = {
    gold: "Złoto",
    silver: "Srebro",
  };

  const formatPL = (val: number, maxFrac = 2): string =>
    Number(val)
      .toLocaleString("de-DE", {
        minimumFractionDigits: 0,
        maximumFractionDigits: maxFrac,
      })
      .replace(/\./g, "\u00A0");

  const formatValue = (item: any, column: string) => {
    if (column === "currencyUuid") {
      const currency = item.currency?.currency;
      return currency ?? "Brak";
    }

    if (column === "industry") {
      if (item.industryUuid) {
        return industryMap[item.industryUuid] ?? "Brak";
      }
      return item.industry?.name ?? item.details?.industryName ?? "Brak";
    }

    if (column === "sharesCount") {
      const val = item.sharesCount;
      if (val === null || val === undefined) return "Brak";
      return formatPL(Number(val), 0);
    }

    if (column === "quantity") {
      const val = item.quantity;
      if (val === null || val === undefined) return "Brak";
      if (investmentType.slug === "crypto") {
        return Number(val)
          .toLocaleString("en-US", {
            minimumFractionDigits: 0,
            maximumFractionDigits: 8,
          })
          .replace(/,/g, "\u00A0");
      }
      return formatPL(Number(val), 0);
    }

    if (column === "sharesValue") {
      const val = item.sharesValue;
      if (val === null || val === undefined) return "Brak";
      const formatted = formatPL(Number(val));
      const currency = item.currency?.currency ?? "";
      return currency ? `${formatted} ${currency}` : formatted;
    }

    if (column === "publicationDate" && isStocksOrShares) {
      const dateVal = item.publicationDate ?? item.details?.publicationDate;
      if (!dateVal) return "Brak";
      const d = new Date(dateVal as string);
      return isNaN(d.getTime())
        ? String(dateVal)
        : d.toLocaleDateString("pl-PL");
    }

    if (column === "name" && investmentType.slug === "currencies") {
      return item.currency?.currency ?? "Brak";
    }

    if (
      column === "interestRate" &&
      (investmentType.slug === "treasury_bonds" ||
        investmentType.slug === "loans")
    ) {
      const value = item.details?.interestRate;
      if (value === null || value === undefined || value === "") return "Brak";
      return `${value}%`;
    }

    if (column === "entity" && investmentType.slug === "loans") {
      const val = item.details?.entity ?? item.entity;
      if (val === null || val === undefined || val === "") return "Brak";
      return val;
    }

    if (column === "loanType") {
      const value = item.loanType ?? item.details?.loanType;
      if (value === null || value === undefined || value === "") return "Brak";
      return LOAN_TYPE_LABELS[value] ?? value;
    }

    if (column === "interestType") {
      const value =
        item.details?.interestRateType ?? item.details?.interestType;
      if (value === null || value === undefined || value === "") return "Brak";
      return INTEREST_TYPE_LABELS[value] ?? value;
    }

    if (column === "name" && investmentType.slug === "real_estate") {
      const propertyType = item.details?.propertyType;
      if (!propertyType) return item.name ?? "Brak";
      if (propertyType === "other") {
        return item.details?.propertyTypeOther ?? "Brak";
      }
      return PROPERTY_TYPE_LABELS[propertyType] ?? propertyType;
    }

    if (column === "name" && investmentType.slug === "precious_metals") {
      const metalType = item.details?.metalType;
      if (!metalType) return item.name ?? "Brak";
      if (metalType === "other") {
        return item.details?.metalTypeOther ?? "Brak";
      }
      return METAL_TYPE_LABELS[metalType] ?? metalType;
    }

    if (column === "city") {
      const val = item.details?.city;
      if (val === null || val === undefined || val === "") return "Brak";
      return val;
    }

    if (column === "pricePerSqm") {
      const sqmValue = item.details?.pricePerSqm;
      if (sqmValue === null || sqmValue === undefined) return "Brak";
      return formatPL(Number(sqmValue));
    }

    if (investmentType.slug === "investment_policy") {
      if (column === "contractDate") {
        const dateVal = item.details?.contractDate;
        if (!dateVal) return "Brak";
        const d = new Date(dateVal);
        return isNaN(d.getTime()) ? dateVal : d.toLocaleDateString("pl-PL");
      }

      if (column === "durationDate") {
        const dateVal = item.details?.durationDate;
        if (dateVal === null || dateVal === undefined) return "Bezterminowo";
        if (typeof dateVal === "string" && dateVal !== "") {
          const d = new Date(dateVal);
          if (isNaN(d.getTime())) return "Brak";
          if (
            d.getFullYear() === 2000 &&
            d.getMonth() === 0 &&
            d.getDate() === 1
          )
            return "Brak";
          return d.toLocaleDateString("pl-PL");
        }
        return "Brak";
      }
    }

    if (investmentType.slug === "loans") {
      if (column === "contractDate") {
        const dateVal = item.details?.contractDate;
        if (!dateVal) return "Brak";
        const d = new Date(dateVal);
        return isNaN(d.getTime()) ? dateVal : d.toLocaleDateString("pl-PL");
      }

      if (column === "durationDate") {
        const dateVal = item.details?.durationDate;
        if (dateVal === null || dateVal === undefined) return "Bezterminowo";
        if (typeof dateVal === "string" && dateVal !== "") {
          const d = new Date(dateVal);
          if (isNaN(d.getTime())) return "Brak";
          if (
            d.getFullYear() === 2000 &&
            d.getMonth() === 0 &&
            d.getDate() === 1
          )
            return "Brak";
          return d.toLocaleDateString("pl-PL");
        }
        return "Brak";
      }
    }

    const value = detailsFields.includes(column)
      ? item.details?.[column]
      : item[column];

    if (value === null || value === undefined || value === "") return "Brak";

    if (column === "interestRate") return `${value}%`;
    if (column === "interestPayment" || column === "premiumPayment")
      return PAYMENT_LABELS[value] ?? value;
    if (column === "autoRenewal")
      return value === true || value === 1 ? "Tak" : "Nie";
    if (column === "primaryValue") {
      const numVal = typeof value === "number" ? value : Number(value);
      if (!isNaN(numVal)) {
        return formatPL(numVal);
      }
      return value;
    }
    if (column === "weight") {
      return typeof value === "number" ? `${formatPL(value)}g` : `${value}g`;
    }
    if (column === "area") {
      return typeof value === "number" ? formatPL(value) : value;
    }
    if (column === "mass") return `${value}g`;

    return value;
  };

  const isAnyModalOpen = !!(
    selectedTransactionItem ||
    selectedDetailsItem ||
    addTransactionItem ||
    editTransactionItem ||
    closeItem ||
    deleteItem ||
    deleteShareItem ||
    editItem ||
    selectedShareItem ||
    sellSharesItem ||
    editSharesItem ||
    editCompanyItem ||
    lightboxItem
  );
  const dragEnabled = isDragEnabled && !isAnyModalOpen;

  return (
    <>
      <Tooltip id="asset-info-tooltip" place="top" className="tooltip" />
      <div
        ref={tileRef}
        className={`asset-tile ${
          dragEnabled && isDragging ? "asset-tile--dragging" : ""
        } ${dragEnabled && isDragOver ? "asset-tile--drag-over" : ""} ${
          isExpanded ? "asset-tile--expanded" : ""
        } ${!dragEnabled ? "asset-tile--drag-disabled" : ""} ${
          !authorizedUser?.package ||
          authorizedUser.package === PACKET_ENUM.BASIC
            ? "asset-tile--no-access"
            : ""
        }`}
        draggable={dragEnabled}
        onDragStart={
          dragEnabled
            ? (e) => {
                isDraggingRef.current = true;
                onDragStart(e, index);
              }
            : undefined
        }
        onDragEnter={dragEnabled ? () => onDragEnter(index) : undefined}
        onDragEnd={
          dragEnabled
            ? () => {
                isDraggingRef.current = false;
                onDragEnd();
              }
            : undefined
        }
        onDragOver={dragEnabled ? (e) => e.preventDefault() : undefined}
        data-tile-index={index}
        onTouchStart={dragEnabled ? () => onTouchStart(index) : undefined}
        onTouchMove={dragEnabled ? onTouchMove : undefined}
        onTouchEnd={dragEnabled ? onTouchEnd : undefined}
      >
        <div className="asset-tile__header" onClick={handleToggle}>
          <div
            className="asset-tile__drag-handle"
            onClick={(e) => e.stopPropagation()}
          >
            {isDragEnabled && <SVG type={SVG_ENUM.DRAG_HANDLE} />}
          </div>
          <span className="asset-tile__title">
            {ASSET_CATEGORY_NAMES[investmentType.slug as AssetCategorySlug] ??
              investmentType.name}
          </span>
          {items.length > 0 && (
            <div
              className="asset-tile__header-actions"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                className={`asset-tile__action-btn${
                  !isStocksOrShares || !hasAccessToSpeaker
                    ? " asset-tile__action-btn--disabled"
                    : ""
                }`}
                onClick={() => isStocksOrShares && handleSpeakerClick()}
              >
                <SVG type={SVG_ENUM.ANNOUCEMENT} />
                <span
                  className={`asset-tile__action-badge${
                    isStocksOrShares && totalUnviewedPosts > 0
                      ? " asset-tile__action-badge--new"
                      : ""
                  }`}
                >
                  {isStocksOrShares ? totalUnviewedPosts : 0}
                </span>
              </button>
            </div>
          )}
          <div
            className={`asset-tile__chevron ${
              isExpanded ? "asset-tile__chevron--expanded" : ""
            }`}
          >
            <SVG type={SVG_ENUM.CHEVRON_DOWN} />
          </div>
        </div>

        <div
          className={`asset-tile__content-wrapper ${
            isExpanded ? "asset-tile__content-wrapper--expanded" : ""
          }`}
        >
          <div className="asset-tile__content-inner">
            {items.length > 0 && (
              <div className="asset-tile__content">
                <div className="asset-tile__table">
                  <div className="asset-tile__table-header">
                    {columns.map((col) => (
                      <div
                        key={col.key}
                        className="asset-tile__table-cell"
                        style={
                          col.width
                            ? {
                                flex: `0 0 ${col.width}px`,
                                minWidth: col.width,
                              }
                            : undefined
                        }
                      >
                        {col.label ?? columnLabels[col.key] ?? col.key}
                      </div>
                    ))}
                    {hasFixedWidths && <div style={{ flex: 1 }} />}
                    <div className="asset-tile__table-cell asset-tile__table-cell--actions asset-tile__table-cell--actions-desktop" />
                  </div>
                  {items.map((item) => {
                    // const photoFiles =
                    //   investmentType.slug === "artwork" ||
                    //   investmentType.slug === "real_estate"
                    //     ? (item.files ?? []).filter(
                    //         (f: any) => f.role === "photo",
                    //       )
                    //     : [];

                    const renderCellContent = (col: ColumnDef) => {
                      if (col.value !== undefined) {
                        return <>{col.value}</>;
                      }
                      if (
                        col.key === "name" &&
                        investmentType.slug === "real_estate"
                      ) {
                        return <span>{formatValue(item, col.key)}</span>;
                      }
                      return (
                        <>
                          {formatValue(item, col.key) !== "Brak" &&
                          col.computePrefix
                            ? col.computePrefix(item)
                            : ""}
                          {formatValue(item, col.key)}
                          {(() => {
                            if (formatValue(item, col.key) === "Brak")
                              return "";
                            if (col.computeSuffix) {
                              const s = col.computeSuffix(item);
                              return s ? s : "";
                            }
                            return col.suffix ?? "";
                          })()}
                        </>
                      );
                    };

                    const isInvestmentPolicy =
                      investmentType.slug === "investment_policy";
                    const isTreasuryBonds =
                      investmentType.slug === "treasury_bonds";
                    const isArtwork = investmentType.slug === "artwork";
                    const isPreciousStones =
                      investmentType.slug === "precious_stones";
                    const isPreciousMetals =
                      investmentType.slug === "precious_metals";
                    const isRealEstate = investmentType.slug === "real_estate";
                    const isLoan = investmentType.slug === "loans";
                    const isOther =
                      !isStocksOrShares &&
                      !isInvestmentPolicy &&
                      !isTreasuryBonds &&
                      !isArtwork &&
                      !isPreciousStones &&
                      !isPreciousMetals &&
                      !isRealEstate &&
                      !isLoan &&
                      !hasTransactions;
                    const isDetails = !isStocksOrShares && !hasTransactions;

                    const activeUuid =
                      selectedShareItem?.uuid ??
                      selectedDetailsItem?.uuid ??
                      selectedTransactionItem?.uuid ??
                      null;

                    const isActive = activeUuid === item.uuid;

                    return (
                      <div
                        key={item.uuid}
                        className={`asset-tile__table-row${
                          hasTransactions || isStocksOrShares || isDetails
                            ? " asset-tile__table-row--clickable"
                            : ""
                        }${isActive ? " asset-tile__table-row--active" : ""}`}
                        onClick={() => {
                          if (isDraggingRef.current) return;
                          if (isStocksOrShares) setSelectedShareItem(item);
                          else if (hasTransactions)
                            setSelectedTransactionItem(item);
                          else setSelectedDetailsItem(item);
                        }}
                      >
                        {/* Desktop: pozioma tabela */}
                        {columns.map((col) => (
                          <div
                            key={col.key}
                            className="asset-tile__table-cell asset-tile__table-cell--desktop"
                            style={
                              col.width
                                ? {
                                    flex: `0 0 ${col.width}px`,
                                    minWidth: col.width,
                                  }
                                : undefined
                            }
                          >
                            {col.key === columns[0].key &&
                              item.additionalInfo && (
                                <span
                                  className="asset-tile__info-icon"
                                  data-tooltip-id="asset-info-tooltip"
                                  data-tooltip-content={item.additionalInfo}
                                >
                                  <SVG type={SVG_ENUM.INFO} />
                                </span>
                              )}
                            {renderCellContent(col)}
                          </div>
                        ))}
                        {hasFixedWidths && (
                          <div
                            className="asset-tile__table-cell--desktop"
                            style={{ flex: 1 }}
                          />
                        )}
                        <div className="asset-tile__table-cell asset-tile__table-cell--actions asset-tile__table-cell--actions-desktop">
                          <button
                            className={`asset-tile__action-btn${
                              !isStocksOrShares || !hasAccessToSpeaker
                                ? " asset-tile__action-btn--disabled"
                                : ""
                            }`}
                            onClick={(e) => {
                              e.stopPropagation();
                              if (isStocksOrShares) {
                                handleSpeakerClick(
                                  (item as any).id,
                                  item.origin,
                                );
                              }
                            }}
                          >
                            <SVG type={SVG_ENUM.ANNOUCEMENT} />
                            <span
                              className={`asset-tile__action-badge${
                                isStocksOrShares &&
                                (unviewedPostCount?.[(item as any).id] || 0) > 0
                                  ? " asset-tile__action-badge--new"
                                  : ""
                              }`}
                            >
                              {isStocksOrShares
                                ? unviewedPostCount?.[(item as any).id] || 0
                                : 0}
                            </span>
                          </button>
                          <ContextMenu
                            data={item}
                            position="left"
                            items={[
                              ...(isOwn && !isStocksOrShares
                                ? [
                                    {
                                      name: "Edytuj",
                                      svg: SVG_ENUM.EDIT,
                                      action: (d: any) => setEditItem(d),
                                    },
                                  ]
                                : []),
                              ...(investmentType.slug === "investment_policy" &&
                              !isArchived
                                ? [
                                    {
                                      name: "Zamknij aktywo",
                                      svg: SVG_ENUM.CLOSE,
                                      action: (d: any) =>
                                        setCloseItem({
                                          item: d,
                                          config:
                                            CLOSE_INVESTMENT_CONFIGS.policy,
                                        }),
                                    },
                                  ]
                                : []),
                              ...(investmentType.slug === "treasury_bonds" &&
                              !isArchived
                                ? [
                                    {
                                      name: "Zamknij aktywo",
                                      svg: SVG_ENUM.CLOSE,
                                      action: (d: any) =>
                                        setCloseItem({
                                          item: d,
                                          config:
                                            CLOSE_INVESTMENT_CONFIGS.treasuryBonds,
                                        }),
                                    },
                                  ]
                                : []),
                              ...((investmentType.slug === "artwork" ||
                                investmentType.slug === "precious_stones") &&
                              !isArchived
                                ? [
                                    {
                                      name: "Zamknij aktywo",
                                      svg: SVG_ENUM.CLOSE,
                                      action: (d: any) =>
                                        setCloseItem({
                                          item: d,
                                          config:
                                            investmentType.slug ===
                                            "precious_stones"
                                              ? CLOSE_INVESTMENT_CONFIGS.preciousStones
                                              : CLOSE_INVESTMENT_CONFIGS.artwork,
                                        }),
                                    },
                                  ]
                                : []),
                              ...(isPreciousMetals && !isArchived
                                ? [
                                    {
                                      name: "Zamknij aktywo",
                                      svg: SVG_ENUM.CLOSE,
                                      action: (d: any) =>
                                        setCloseItem({
                                          item: d,
                                          config:
                                            CLOSE_INVESTMENT_CONFIGS.preciousMetals,
                                        }),
                                    },
                                  ]
                                : []),
                              ...(investmentType.slug === "real_estate" &&
                              !isArchived
                                ? [
                                    {
                                      name: "Zamknij aktywo",
                                      svg: SVG_ENUM.CLOSE,
                                      action: (d: any) =>
                                        setCloseItem({
                                          item: d,
                                          config:
                                            CLOSE_INVESTMENT_CONFIGS.realEstate,
                                        }),
                                    },
                                  ]
                                : []),
                              ...(investmentType.slug === "loans" && !isArchived
                                ? [
                                    {
                                      name: "Zamknij aktywo",
                                      svg: SVG_ENUM.CLOSE,
                                      action: (d: any) =>
                                        setCloseItem({
                                          item: d,
                                          config: CLOSE_INVESTMENT_CONFIGS.loan,
                                        }),
                                    },
                                  ]
                                : []),
                              ...(isOther && !isArchived
                                ? [
                                    {
                                      name: "Zamknij aktywo",
                                      svg: SVG_ENUM.CLOSE,
                                      action: (d: any) =>
                                        setCloseItem({
                                          item: d,
                                          config:
                                            CLOSE_INVESTMENT_CONFIGS.otherAsset,
                                        }),
                                    },
                                  ]
                                : []),
                              ...(isStocksOrShares && item.origin === "other"
                                ? [
                                    {
                                      name: "Edytuj",
                                      svg: SVG_ENUM.EDIT,
                                      action: (d: any) => setEditCompanyItem(d),
                                    },
                                  ]
                                : []),
                              ...(hasTransactions
                                ? [
                                    {
                                      name: "Dodaj  transakcję",
                                      svg: SVG_ENUM.COINS_HAND,
                                      action: (d: any) =>
                                        setAddTransactionItem({
                                          item: d,
                                          defaultType:
                                            isOwn && !isArchived
                                              ? "sell"
                                              : "buy",
                                        }),
                                    },
                                  ]
                                : []),
                              ...(isStocksOrShares && !isArchived
                                ? [
                                    {
                                      name: "Dodaj transakcję",
                                      svg: SVG_ENUM.COINS_HAND,
                                      action: (d: any) => openSellModal(d),
                                    },
                                  ]
                                : []),
                              ...(isOwn && !isStocksOrShares
                                ? [
                                    {
                                      name: "Usuń",
                                      svg: SVG_ENUM.TRASH,
                                      action: (d: any) => setDeleteItem(d),
                                    },
                                  ]
                                : []),
                              ...(isOwn && isStocksOrShares
                                ? [
                                    {
                                      name: "Usuń",
                                      svg: SVG_ENUM.TRASH,
                                      action: (d: any) => setDeleteShareItem(d),
                                    },
                                  ]
                                : []),
                            ]}
                          >
                            <button className="asset-tile__action-btn">
                              <SVG type={SVG_ENUM.DOTS} />
                            </button>
                          </ContextMenu>
                        </div>

                        {/* Mobile: lista par etykieta–wartość */}
                        <div
                          className={`asset-tile__mobile-list${
                            hasTransactions || isStocksOrShares || isDetails
                              ? " asset-tile__mobile-list--clickable"
                              : ""
                          }`}
                          onClick={() => {
                            if (isDraggingRef.current) return;
                            if (isStocksOrShares) setSelectedShareItem(item);
                            else if (hasTransactions)
                              setSelectedTransactionItem(item);
                            else setSelectedDetailsItem(item);
                          }}
                        >
                          {columns.map((col) => (
                            <div
                              key={col.key}
                              className="asset-tile__mobile-row"
                            >
                              <span className="asset-tile__mobile-label">
                                {col.label ?? columnLabels[col.key] ?? col.key}
                              </span>
                              <span className="asset-tile__mobile-value">
                                {renderCellContent(col)}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>

        <TransactionsSideModal
          isOpen={!!selectedShareItem}
          onClose={() => setSelectedShareItem(null)}
          company={selectedShareItem}
          setEditTransaction={(company) => openEditSharesModal(company)}
          onAddTransactionClick={() => openSellModal(selectedShareItem)}
        />

        <SellSharesModal
          isOpen={!!sellSharesItem}
          onSuccess={returnToSidebar}
          onClose={returnToSidebar}
          hardClose={() => setSellSharesItem(null)}
          company={sellSharesItem}
        />

        <AddSharesModal
          isOpen={!!editSharesItem}
          onSuccess={returnToSidebar}
          onClose={returnToSidebar}
          hardClose={() => setEditSharesItem(null)}
          company={editSharesItem}
        />

        <InvestmentPhotoLightbox
          isOpen={!!lightboxItem}
          onClose={() => setLightboxItem(null)}
          files={(lightboxItem?.files ?? []).filter(
            (f: any) => f.role === "photo",
          )}
          title={lightboxItem?.name}
        />

        <InvestmentTransactionsSideModal
          isOpen={!!selectedTransactionItem}
          onClose={() => setSelectedTransactionItem(null)}
          item={selectedTransactionItem}
          investmentType={investmentType}
          isOwn={isOwn}
          isArchived={isArchived}
          onAddTransaction={(type, currentQuantity) =>
            setAddTransactionItem({
              item: selectedTransactionItem,
              defaultType: type,
              currentQuantity,
            })
          }
          onEditTransaction={(tx, currentQuantity) =>
            setEditTransactionItem({
              item: selectedTransactionItem,
              tx,
              currentQuantity,
            })
          }
        />

        <AssetDetailsSideModal
          isOpen={!!selectedDetailsItem}
          onClose={() => setSelectedDetailsItem(null)}
          item={selectedDetailsItem}
          slug={investmentType.slug}
          investmentTypeName={investmentType.name}
        />

        <AddInvestmentTransactionModal
          isOpen={!!addTransactionItem}
          onClose={() => setAddTransactionItem(null)}
          onSuccess={() =>
            setSelectedTransactionItem(addTransactionItem?.item ?? null)
          }
          item={addTransactionItem?.item ?? null}
          investmentType={investmentType}
          defaultType={addTransactionItem?.defaultType ?? "sell"}
          isOwn={isOwn}
          isArchived={isArchived}
          currentQuantity={addTransactionItem?.currentQuantity}
        />

        <AddInvestmentTransactionModal
          isOpen={!!editTransactionItem}
          onClose={() => setEditTransactionItem(null)}
          item={editTransactionItem?.item ?? null}
          investmentType={investmentType}
          defaultType={editTransactionItem?.tx?.type ?? "sell"}
          isOwn={false}
          currentQuantity={editTransactionItem?.currentQuantity}
          editTransaction={editTransactionItem?.tx ?? null}
        />

        <CloseInvestmentModal
          isOpen={!!closeItem}
          onClose={() => setCloseItem(null)}
          item={closeItem?.item ?? null}
          config={closeItem?.config ?? CLOSE_INVESTMENT_CONFIGS.otherAsset}
        />

        <DeleteInvestmentModal
          isOpen={!!deleteItem}
          onClose={() => setDeleteItem(null)}
          item={deleteItem}
        />

        <DeleteCompanyModal
          isOpen={!!deleteShareItem}
          onClose={() => setDeleteShareItem(null)}
          company={deleteShareItem}
        />

        <EditInvestmentModal
          isOpen={!!editItem}
          onClose={() => setEditItem(null)}
          item={editItem}
          slug={investmentType.slug}
        />

        <AddCompanyModal
          isOpen={!!editCompanyItem && isAdmin}
          onClose={() => setEditCompanyItem(null)}
          company={editCompanyItem}
          onSuccess={() => setEditCompanyItem(null)}
        />

        <AddCompanyClientModal
          isOpen={!!editCompanyItem && !isAdmin}
          onClose={() => setEditCompanyItem(null)}
          company={editCompanyItem}
          onSuccess={() => setEditCompanyItem(null)}
        />
      </div>
    </>
  );
};

export default AssetTile;
